/**
 * Name: New customer
 * Author: @Gaurav
 * Date: 20-07-2021
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Sidebar from '../../components/SideBar';
import NavBar from '../../components/NavBarSearchSong';
import Settings from '../../assets/Settings.png';
import EyeIcon from '../../assets/eye_icn.svg';
import HideEyeIcon from '../../assets/hide-private-hidden.svg';
import email_icn from '../../assets/email.svg';
import call from '../../assets/call.svg';
import RoundButton from '../../components/RoundButton';
import '../NewCustomer/NewCustomer.scss';
import { _showAlert } from '../../utils';
import Dropdown from '../../components/Dropdown';
import { editorAccess, royalty } from '../../generalConstant';
import { Autocomplete, Checkbox, TextField } from '@mui/material';
import { CircularProgress } from '@material-ui/core';
import { useGetGroupCustomers, useGetGroups } from '../../api/groups';
import { getCustomerPassword, useGetCustomerGroups } from '../../api/customer';
import { useGetEditors } from '../../api/editor';
import { buildCustomerSavePayload } from '../../utils/safeEditPayloads';

function NewCustomer(props) {
  const dispatch = useDispatch();
  const { newCustomerData, status } = useSelector((state) => ({
    newCustomerData: state.newCustomer.result,
    status: state.newCustomer.status,
  }));

  const authData = JSON.parse(sessionStorage.getItem('authData') || '{}');
  const currentUserRole = authData?.user?.userRole;
  const isEditor = currentUserRole === 3;

  const customerData = props?.location?.state || props?.history?.location?.state;
  const customerId = customerData?.id;
  const { groups, mutateGroups } = useGetGroups(isEditor);
  const { editors } = useGetEditors('', isEditor);
  const { groups: customerGroups = [], isGroupsLoading, groupsError } = useGetCustomerGroups(isEditor ? null : customerId);
  const { mutateCustomers } = useGetGroupCustomers();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedEditors, setSelectedEditors] = useState(null);
  const editedFields = useRef(new Set());
  const passwordRequestId = useRef(0);

  // Filter out groups that are already assigned to the customer
  const availableGroups = useMemo(() => {
    if (!isEditor) {
      if (!customerGroups?.length) return groups.filter((g) => g.name !== 'All Customers');
      return groups.filter((g) => g.name !== 'All Customers' && !customerGroups.some(cg => cg.id === g.id));
    }
    return [];
  }, [groups, customerGroups, isEditor]);

  const defaultFormState = {
    firstname: '',
    lastname: '',
    business_name: '',
    username: '',
    password: '',
    phone: '',
    email: '',
    address: '',
  };
  const [formFields, setFormFields] = useState(defaultFormState);
  const [togglePassword, setTogglePassword] = useState(true);
  const [passwordLoaded, setPasswordLoaded] = useState(false);
  const [passwordEdited, setPasswordEdited] = useState(false);
  const [isFetchingPassword, setIsFetchingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [errors, setErrors] = useState();
  const [readoOnlyFields, setReadOnlyFields] = useState({
    username: true,
    password: true,
  });
  const [isRoyalty, setIsRoyalty] = useState(royalty[0]);
  let isRoyaltyValue = isRoyalty?.value === 'true';

  const [isEditorAccess, setIsEditorAccess] = useState(editorAccess[0]);

  /**
   * it will handle the validation.
   */
  const handleValidation = () => {
    let fields = formFields;
    let errors = {};
    let formIsValid = true;
    const mustCheck = (field) => !formFields.id || editedFields.current.has(field);

    //firstname
    if (mustCheck('firstname') && !fields['firstname']) {
      formIsValid = false;
      errors['firstname'] = 'Cannot be empty';
    }
    //lastname
    if (mustCheck('lastname') && !fields['lastname']) {
      formIsValid = false;
      errors['lastname'] = 'Cannot be empty';
    }
    //business_name
    if (mustCheck('business_name') && !fields['business_name']) {
      formIsValid = false;
      errors['business_name'] = 'Cannot be empty';
    }
    //username
    if (!formFields.id) {
      if (!fields['username']) {
        formIsValid = false;
        errors['username'] = 'Cannot be empty';
      }
    }
    if (formFields.id && editedFields.current.has('username') && !fields['username']) {
      formIsValid = false;
      errors['username'] = 'Cannot be empty';
    }
    //pasword
    if (!formFields.id) {
      if (!fields['password']) {
        formIsValid = false;
        errors['password'] = 'Cannot be empty';
      }
    }
    //phone
    if (mustCheck('phone') && !fields['phone']) {
      formIsValid = false;
      errors['phone'] = 'Cannot be empty';
    }
    //email
    if (mustCheck('email') && !fields['email']) {
      formIsValid = false;
      errors['email'] = 'Cannot be empty';
    }
    //address
    if (mustCheck('address') && !fields['address']) {
      formIsValid = false;
      errors['address'] = 'Cannot be empty';
    }
    if ((!formFields.id || editedFields.current.has('assigned_editors')) &&
        isEditorAccess.value === 'select_editor' && !selectedEditors?.id) {
      formIsValid = false;
      errors['editor'] = 'Cannot be empty';
    }
    setErrors(errors);
    return formIsValid;
  };

  const handleInputChange = (event) => {
    let id = event.target.id;
    let value = event.target.value;
    if (id === 'password') {
      setPasswordEdited(true);
      setPasswordError('');
    }
    editedFields.current.add(id);
    setFormFields({ ...formFields, [id]: value });
  };

  const handleTogglePassword = async () => {
    if (!togglePassword) {
      setTogglePassword(true);
      return;
    }

    if (customerId && !passwordLoaded && !passwordEdited) {
      const requestId = ++passwordRequestId.current;
      setIsFetchingPassword(true);
      setPasswordError('');
      try {
        const password = await getCustomerPassword(customerId);
        if (requestId !== passwordRequestId.current) return;
        if (typeof password !== 'string' || !password) {
          throw new Error('Invalid password response');
        }
        setFormFields((current) => current.password ? current : { ...current, password: password || '' });
        setPasswordLoaded(true);
      } catch (error) {
        if (requestId !== passwordRequestId.current) return;
        setPasswordError(error?.response?.data?.code === 'PASSWORD_DECRYPTION_FAILED'
          ? 'This password cannot be decrypted. Change the password once to view it from then on.'
          : 'Could not load the password. Please try again.');
        return;
      } finally {
        if (requestId === passwordRequestId.current) setIsFetchingPassword(false);
      }
    }

    setTogglePassword(false);
  };

  /**
   * it will handle the new customer api request.
   */
  const handleNewCustomer = async () => {
    if (handleValidation()) {
      const payload = buildCustomerSavePayload({ formFields, editedFields: editedFields.current,
        isRoyaltyValue, isEditorAccess, selectedEditors, selectedGroups, passwordEdited });
      if (formFields.id && Object.keys(payload).length === 1) return;
      try {
        dispatch({
          type: 'NEW_CUSTOMER',
          payload,
        });
      } catch (error) {
        console.error('Error details:', error);
        _showAlert('Error creating/updating customer', 'error', 4000);
      }
    }
  };

  useEffect(() => {
    if (status === 'initiated') {
      setIsLoading(true);
    } else if (newCustomerData && status === 'success') {
      dispatch({ type: 'NEW_CUSTOMER_CLEAR' });
      setFormFields(defaultFormState);
      setIsLoading(false);
      mutateGroups();
      mutateCustomers();
      _showAlert('Customer Details Submitted Successfully', 'success', 4000);
      props.history.push('/customer-list');
    } else if (newCustomerData && status === 'failed') {
      setIsLoading(false);
      _showAlert('Something went wrong', 'error', 4000);
      // props.history.push('/customer-list');
    } else {
      setIsLoading(false);
    }
  }, [newCustomerData, status]);

  useEffect(() => {
    if (selectedEditors?.id && Array.isArray(editors)) {
      const editor = editors.find((item) => item.id === selectedEditors.id);
      if (editor && editor.name !== selectedEditors.name) setSelectedEditors(editor);
    }
  }, [editors, selectedEditors]);

  useEffect(() => {
    passwordRequestId.current += 1;
    editedFields.current = new Set();
    setSelectedEditors(null);
    setSelectedGroups([]);
    setIsEditorAccess(editorAccess[0]);
    setPasswordLoaded(false);
    setPasswordEdited(false);
    setIsFetchingPassword(false);
    setPasswordError('');
    setTogglePassword(true);
    setErrors(undefined);
    setReadOnlyFields({ username: true, password: true });
    if (customerData) {
      setFormFields({ ...defaultFormState, ...customerData, password: '' });
      setIsRoyalty(royalty.find((option) => option.value === String(customerData.royalty_only)) || royalty[0]);
      try {
        const assigned = typeof customerData.assigned_editors === 'string'
          ? JSON.parse(customerData.assigned_editors) : customerData.assigned_editors;
        if (Array.isArray(assigned)) {
          setIsEditorAccess(assigned.includes('all') ? editorAccess[1] : assigned.length ? editorAccess[2] : editorAccess[0]);
          if (assigned.length === 1 && assigned[0] !== 'all') {
            setSelectedEditors({ id: assigned[0], name: assigned[0] });
          }
        }
      } catch (error) {
        // Unchanged editor access is omitted from updates.
      }
    } else {
      setFormFields(defaultFormState);
      setIsRoyalty(royalty[0]);
    }
  }, [customerData]);

  // Apply loaded memberships after resetting state for a different customer.
  useEffect(() => {
    if (!isEditor && customerId && customerGroups?.length && !editedFields.current.has('groupIds')) {
      setSelectedGroups(customerGroups);
    }
  }, [customerGroups, customerId, isEditor]);

  return (
    <>
      <div className="main">
        <div className="sidebar">
          <Sidebar />
        </div>
        <div className="leftPortion">
          <NavBar
            data={formFields.id ? 'Update Customer' : 'New Customer'}
            icon={Settings}
            currentRouteName="Customers"
            breadcrumbsPath={formFields.id ? 'Update Customer' : 'New Customer'}
          />
          <div className="content">
            <div className="btn_row">
              <RoundButton disabled={isLoading} btnText={isLoading ? 'Loading...' : 'Save'} handleClick={handleNewCustomer} />
            </div>
            <div className="new_customer_row">
              <h5>General</h5>
              <div className="field_row">
                <div className="field_col">
                  <label>First Name</label>
                  <input type="text" value={formFields.firstname} onChange={handleInputChange} id="firstname" />
                  <span className="input-error-messages">{errors && errors['firstname']}</span>
                </div>
                <div className="field_col">
                  <label>Last Name</label>
                  <input type="text" value={formFields.lastname} onChange={handleInputChange} id="lastname" />
                  <span className="input-error-messages">{errors && errors['lastname']}</span>
                </div>
                <div className="field_col">
                  <label>Business Name</label>
                  <input type="text" value={formFields.business_name} onChange={handleInputChange} id="business_name" />
                  <span className="input-error-messages">{errors && errors['business_name']}</span>
                </div>
                <div className="field_col">
                  <label>Username</label>
                  <input
                    type="text"
                    value={formFields.username}
                    onChange={handleInputChange}
                    id="username"
                    readOnly={readoOnlyFields.username}
                    onFocus={() => setReadOnlyFields({ ...readoOnlyFields, username: false })}
                  />
                  <span className="input-error-messages">{errors && errors['username']}</span>
                </div>
                <div className="field_col">
                  <label>Password</label>
                  <div className="password_input_wrap">
                    <input
                      type={togglePassword ? 'password' : 'text'}
                      value={formFields.password}
                      onChange={handleInputChange}
                      id="password"
                      placeholder={formFields.id ? 'Click the eye to view current password' : ''}
                      readOnly={isFetchingPassword || readoOnlyFields.password}
                      onFocus={() => setReadOnlyFields({ ...readoOnlyFields, password: false })}
                    />
                    <button
                      type="button"
                      className="password_visibility"
                      onClick={handleTogglePassword}
                      disabled={isFetchingPassword}
                      aria-label={isFetchingPassword ? 'Loading password' : togglePassword ? 'Reveal password' : 'Hide password'}
                      title={isFetchingPassword ? 'Loading password' : togglePassword ? 'Reveal password' : 'Hide password'}
                    >
                      {isFetchingPassword ? <CircularProgress size={20} /> : <img src={togglePassword ? HideEyeIcon : EyeIcon} alt="" />}
                    </button>
                  </div>
                  {passwordError && <span className="input-error-messages" role="alert">{passwordError}</span>}
                  <span className="input-error-messages">{errors && errors['password']}</span>
                </div>
                <div className="field_col">
                  <label>Customer Type</label>
                  <Dropdown options={royalty} value={isRoyalty} setValue={(value) => { setIsRoyalty(value); editedFields.current.add('royalty_only'); }} />
                </div>
                {!isEditor && (
                  <>
                    <div className="field_col">
                      <label>Groups</label>
                      <Autocomplete
                        size="small"
                        multiple
                        disabled={!!customerId && (isGroupsLoading || !!groupsError)}
                        limitTags={3}
                        id="groups-outlined"
                        options={availableGroups}
                        getOptionLabel={(option) => option?.name}
                        disableCloseOnSelect
                        value={selectedGroups}
                        onChange={(event, newValue) => {
                          setSelectedGroups(newValue);
                          editedFields.current.add('groupIds');
                        }}
                        renderOption={(props, option, { selected }) => {
                          const { key, ...optionProps } = props;
                          return (
                            <li key={key} {...optionProps}>
                              <Checkbox
                                style={{ marginRight: 8 }}
                                checked={selected}
                              />
                              {option.name}
                            </li>
                          );
                        }}
                        renderInput={(params) => <TextField {...params} placeholder="Groups" className="no-border" />}
                      />
                    </div>
                    <div className="field_col">
                      <label>Editor </label>
                      <Dropdown options={editorAccess} value={isEditorAccess} setValue={(value) => { setIsEditorAccess(value); editedFields.current.add('assigned_editors'); }} />
                    </div>
                    {isEditorAccess.value === 'select_editor' && (
                      <div className="field_col">
                        <label>Select Editor</label>
                        <Autocomplete
                          size="small"
                          id="editors-outlined"
                          options={editors || []}
                          getOptionLabel={(option) => option?.name || ''}
                          value={selectedEditors}
                          onChange={(event, newValue) => {
                            setSelectedEditors(newValue);
                            editedFields.current.add('assigned_editors');
                          }}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          renderInput={(params) => <TextField {...params} placeholder="Select Editor" className="no-border" />}
                        />
                        <span className="input-error-messages">{errors && errors['editor']}</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              <h5>contact</h5>

              <div className="field_row">
                <div className="field_col icn_field">
                  <label>MobilePhone</label>
                  <input type="text" value={formFields.phone} onChange={handleInputChange} id="phone" />
                  <img src={call} />
                  <span className="input-error-messages">{errors && errors['phone']}</span>
                </div>
                <div className="field_col icn_field">
                  <label>Email</label>
                  <input type="text" value={formFields.email} onChange={handleInputChange} id="email" />
                  <img src={email_icn} />
                  <span className="input-error-messages">{errors && errors['email']}</span>
                </div>
              </div>

              <div className="field_row">
                <div className="field_col">
                  <label>Address</label>
                  <input
                    type="text"
                    placeholder="Franklin Avenue St. Corner"
                    value={formFields.address}
                    onChange={handleInputChange}
                    id="address"
                  />
                  <span className="input-error-messages">{errors && errors['address']}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default NewCustomer;
